import React, { useState, useEffect, useCallback, useRef } from 'react';
import TopMenu from '../../contexts/TopMenu';
import { adminApi } from '../../services/adminApi';
import { authService } from '../../services/auth';
import '../../styles/CelebritiesManager.css';

const DEFAULT_GENDERS = ['men', 'women'];
const DEFAULT_COUNTRIES = [
  'india',
  'usa',
  'london',
  'dubai',
  'singapore',
  'hong_kong',
  'france',
];

function CelebritiesManager({ email, onLogout }) {
  const [celebrities, setCelebrities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Filters
  const [search, setSearch] = useState('');
  const [genderFilter, setGenderFilter] = useState('');

  // Metadata from API
  const [meta, setMeta] = useState(null);

  // Drawer / Editor state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedCeleb, setSelectedCeleb] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [generatingEmbeddings, setGeneratingEmbeddings] = useState(false);
  const [embedJobs, setEmbedJobs] = useState([]);

  // Form fields
  const [formData, setFormData] = useState({
    name: '',
    gender: 'women',
    nationality: 'india',
    bio: '',
    tags: '',
    is_active: true,
  });

  const fileInputRef = useRef(null);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminApi.getCelebrities({
        q: search,
        gender: genderFilter,
        limit: 500,
      });
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.celebrities)
        ? data.celebrities
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.results)
        ? data.results
        : [];
      setCelebrities(list);
    } catch (err) {
      console.error('Failed to load celebrities:', err);
      setError(err.message || 'Failed to load celebrities list');
    } finally {
      setLoading(false);
    }
  }, [search, genderFilter]);

  const fetchMeta = async () => {
    try {
      const metaData = await adminApi.getCelebritiesMeta();
      setMeta(metaData);
    } catch (err) {
      console.warn('Could not fetch celebrity metadata, using defaults:', err);
    }
  };

  useEffect(() => {
    fetchMeta();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchList();
    }, 250);
    return () => clearTimeout(timer);
  }, [fetchList]);

  const openCreateModal = () => {
    setIsCreating(true);
    setSelectedCeleb(null);
    setFormData({
      name: '',
      gender: 'women',
      nationality: 'india',
      bio: '',
      tags: '',
      is_active: true,
    });
    setEmbedJobs([]);
    setError('');
    setMessage('');
    setDrawerOpen(true);
  };

  const openEditDrawer = async (celeb) => {
    try {
      setIsCreating(false);
      setError('');
      setMessage('');
      setDrawerOpen(true);
      
      // Fetch full details
      const detail = await adminApi.getCelebrity(celeb.id || celeb._id);
      setSelectedCeleb(detail);
      setFormData({
        name: getCelebName(detail),
        gender: detail.gender || 'women',
        nationality: detail.nationality || detail.country || (Array.isArray(detail.countries) && detail.countries[0]) || 'india',
        bio: detail.bio || '',
        tags: Array.isArray(detail.tags) ? detail.tags.join(', ') : (detail.tags || ''),
        is_active: detail.is_active !== false,
      });

      // Fetch embed jobs if existing
      fetchEmbedJobs(detail.id || detail._id);
    } catch (err) {
      console.error('Failed to get celebrity details:', err);
      setError('Could not load celebrity details: ' + err.message);
    }
  };

  const fetchEmbedJobs = async (id) => {
    try {
      const jobs = await adminApi.getCelebrityEmbedJobs(id);
      setEmbedJobs(Array.isArray(jobs) ? jobs : (jobs.jobs || []));
    } catch (e) {
      // Non-critical
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Celebrity name is required');
      return;
    }

    try {
      setError('');
      setMessage('');
      const payload = {
        name: formData.name.trim(),
        celebrity_name: formData.name.trim(),
        gender: formData.gender,
        nationality: formData.nationality,
        country: formData.nationality,
        bio: formData.bio,
        tags: formData.tags ? formData.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        is_active: Boolean(formData.is_active),
      };

      if (isCreating) {
        const created = await adminApi.createCelebrity(payload);
        setMessage('Celebrity created successfully!');
        setIsCreating(false);
        setSelectedCeleb(created);
        fetchList();
      } else {
        const id = selectedCeleb.id || selectedCeleb._id;
        const updated = await adminApi.updateCelebrity(id, payload);
        setMessage('Celebrity updated successfully!');
        setSelectedCeleb(updated);
        fetchList();
      }
    } catch (err) {
      setError(err.message || 'Failed to save celebrity');
    }
  };

  const handleDelete = async () => {
    if (!selectedCeleb) return;
    const confirmDelete = window.confirm(`Are you sure you want to delete ${getCelebName(selectedCeleb)}?`);
    if (!confirmDelete) return;

    try {
      const id = selectedCeleb.id || selectedCeleb._id;
      await adminApi.deleteCelebrity(id);
      setDrawerOpen(false);
      setSelectedCeleb(null);
      fetchList();
    } catch (err) {
      setError(err.message || 'Failed to delete celebrity');
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedCeleb) return;

    try {
      setUploadingImages(true);
      setError('');
      const id = selectedCeleb.id || selectedCeleb._id;
      const updated = await adminApi.uploadCelebrityImages(id, files);
      setSelectedCeleb(updated);
      setMessage(`Successfully uploaded ${files.length} image(s)!`);
      fetchList();
    } catch (err) {
      setError('Image upload failed: ' + err.message);
    } finally {
      setUploadingImages(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteImage = async (filename) => {
    if (!selectedCeleb) return;
    try {
      setError('');
      const id = selectedCeleb.id || selectedCeleb._id;
      const updated = await adminApi.deleteCelebrityImage(id, filename);
      setSelectedCeleb(updated);
      fetchList();
    } catch (err) {
      setError('Failed to delete image: ' + err.message);
    }
  };

  const handleMoveImage = async (index, direction) => {
    if (!selectedCeleb) return;
    const images = [...getImageList(selectedCeleb)];
    if (images.length === 0) return;
    const targetIdx = index + direction;
    if (targetIdx < 0 || targetIdx >= images.length) return;

    const temp = images[index];
    images[index] = images[targetIdx];
    images[targetIdx] = temp;

    const filenames = images.map((img) => img.filename || (typeof img === 'string' ? img.split('/').pop() : img));
    try {
      const id = selectedCeleb.id || selectedCeleb._id;
      const updated = await adminApi.reorderCelebrityImages(id, filenames);
      setSelectedCeleb(updated);
      fetchList();
    } catch (err) {
      setError('Failed to reorder images: ' + err.message);
    }
  };

  const handleRegenerateEmbeddings = async () => {
    if (!selectedCeleb) return;
    try {
      setGeneratingEmbeddings(true);
      setError('');
      const id = selectedCeleb.id || selectedCeleb._id;
      await adminApi.regenerateCelebrityEmbeddings(id);
      setMessage('Embedding generation task queued!');
      setTimeout(() => fetchEmbedJobs(id), 1000);
    } catch (err) {
      setError('Failed to regenerate embeddings: ' + err.message);
    } finally {
      setGeneratingEmbeddings(false);
    }
  };

  const getCelebName = (celeb) => {
    if (!celeb) return 'Unnamed';
    return celeb.celebrity_name || celeb.name || celeb.title || celeb.displayName || 'Unnamed';
  };

  const getImageList = (celeb) => {
    if (!celeb) return [];
    if (Array.isArray(celeb.images) && celeb.images.length > 0) return celeb.images;
    if (Array.isArray(celeb.files) && celeb.files.length > 0) return celeb.files;
    return [];
  };

  const getImageCount = (celeb) => {
    if (!celeb) return 0;
    if (typeof celeb.image_count === 'number') return celeb.image_count;
    if (typeof celeb.uploaded_image_count === 'number') return celeb.uploaded_image_count;
    return getImageList(celeb).length;
  };

  const getCoverUrl = (celeb) => {
    if (!celeb) return 'https://placehold.co/80x80/e2e8f0/64748b?text=?';
    if (celeb.cover_image) {
      if (typeof celeb.cover_image === 'string') return celeb.cover_image;
      if (typeof celeb.cover_image === 'object') {
        return celeb.cover_image.url || celeb.cover_image.readable_url || '';
      }
    }
    const list = getImageList(celeb);
    if (list.length > 0) {
      const first = list[0];
      return typeof first === 'string' ? first : (first.url || first.readable_url || '');
    }
    return 'https://placehold.co/80x80/e2e8f0/64748b?text=?';
  };

  const getNationality = (celeb) => {
    if (!celeb) return '—';
    if (celeb.nationality) return celeb.nationality;
    if (celeb.country) return celeb.country;
    if (Array.isArray(celeb.countries) && celeb.countries.length > 0) {
      return celeb.countries.join(', ');
    }
    if (Array.isArray(celeb.influence_by_country) && celeb.influence_by_country.length > 0) {
      return celeb.influence_by_country.map((c) => c.country || c).filter(Boolean).join(', ');
    }
    return '—';
  };

  const formatTags = (celeb) => {
    if (!celeb) return [];
    let list = [];
    const rawTags = celeb.tags;
    if (Array.isArray(rawTags)) list = [...rawTags];
    else if (typeof rawTags === 'string' && rawTags.trim()) {
      list = rawTags.split(',').map((t) => t.trim());
    }

    if (list.length === 0) {
      if (Array.isArray(celeb.body_shape)) list.push(...celeb.body_shape);
      if (Array.isArray(celeb.skin_tone)) list.push(...celeb.skin_tone);
    }
    return list.filter(Boolean);
  };

  const countries = meta?.countries || DEFAULT_COUNTRIES;
  const genders = meta?.genders || DEFAULT_GENDERS;

  return (
    <div className="celeb-manager-root">
      <TopMenu pageTitle="Celebrities Admin" email={email} onLogout={onLogout} />

      <div className="celeb-main">
        <div className="celeb-header">
          <div>
            <h1>Celebrity Persona Manager</h1>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '14px' }}>
              Manage celebrity profiles, image references, and face embeddings for AI look generation
            </p>
          </div>
          <div className="celeb-header-actions">
            <button className="celeb-btn-primary" onClick={openCreateModal}>
              + Add Celebrity
            </button>
          </div>
        </div>

        {error && (
          <div className="celeb-alert error" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{error}</span>
            {error.includes('Admin session') && (
              <button
                className="celeb-btn-primary"
                style={{ padding: '4px 12px', fontSize: '12px' }}
                onClick={() => authService.initiateGoogleLogin(window.location.pathname)}
              >
                Authenticate Session
              </button>
            )}
          </div>
        )}
        {message && <div className="celeb-alert success">{message}</div>}

        <div className="celeb-toolbar">
          <input
            type="search"
            className="celeb-search-input"
            placeholder="Search by celebrity name or tag..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="celeb-select"
            value={genderFilter}
            onChange={(e) => setGenderFilter(e.target.value)}
          >
            <option value="">All Genders</option>
            <option value="women">Women</option>
            <option value="men">Men</option>
          </select>
        </div>

        <div className="celeb-table-card">
          <div className="celeb-table-scroll">
            <table className="celeb-table">
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>Photo</th>
                  <th>Celebrity</th>
                  <th>Gender</th>
                  <th>Nationality</th>
                  <th>Tags</th>
                  <th>Photos</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const safeList = Array.isArray(celebrities) ? celebrities : [];
                  if (loading && safeList.length === 0) {
                    return (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                          Loading celebrities...
                        </td>
                      </tr>
                    );
                  }
                  if (safeList.length === 0) {
                    return (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                          No celebrities found matching your criteria.
                        </td>
                      </tr>
                    );
                  }
                  return safeList.map((c, index) => {
                    const id = c.id || c._id || `celeb-${index}`;
                    const name = getCelebName(c);
                    const imgCount = getImageCount(c);
                    const formattedTags = formatTags(c);
                    const nationality = getNationality(c);
                    return (
                      <tr key={id} className="clickable" onClick={() => openEditDrawer(c)}>
                        <td>
                          <img
                            src={getCoverUrl(c)}
                            alt={name}
                            className="celeb-thumb"
                            onError={(e) => {
                              e.target.src = 'https://placehold.co/80x80/e2e8f0/64748b?text=?';
                            }}
                          />
                        </td>
                        <td style={{ fontWeight: 600, color: '#0f172a' }}>{name}</td>
                        <td>
                          <span className={`celeb-badge gender-${(c.gender || 'women').toLowerCase()}`}>
                            {c.gender || 'women'}
                          </span>
                        </td>
                        <td style={{ textTransform: 'capitalize' }}>{nationality}</td>
                        <td>
                          {formattedTags.length > 0 ? (
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {formattedTags.slice(0, 3).map((tag, i) => (
                                <span
                                  key={i}
                                  style={{
                                    fontSize: '11px',
                                    background: '#f1f5f9',
                                    color: '#334155',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                  }}
                                >
                                  {tag.replace('_', ' ')}
                                </span>
                              ))}
                              {formattedTags.length > 3 && (
                                <span style={{ fontSize: '11px', color: '#64748b' }}>
                                  +{formattedTags.length - 3}
                                </span>
                              )}
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>{imgCount}</td>
                        <td>
                          <span className={`celeb-badge ${c.is_active !== false ? 'active' : 'inactive'}`}>
                            {c.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Editor Drawer */}
      {drawerOpen && (
        <div className="celeb-drawer-backdrop" onClick={() => setDrawerOpen(false)}>
          <div className="celeb-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="celeb-drawer-header">
              <h2>{isCreating ? 'Add New Celebrity' : `Edit: ${selectedCeleb?.name || ''}`}</h2>
              <button className="celeb-close-btn" onClick={() => setDrawerOpen(false)}>
                &times;
              </button>
            </div>

            <div className="celeb-drawer-body">
              {error && <div className="celeb-alert error">{error}</div>}
              {message && <div className="celeb-alert success">{message}</div>}

              <form id="celeb-form" onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div className="celeb-form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    className="celeb-form-input"
                    placeholder="e.g. Deepika Padukone"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="celeb-form-group">
                    <label>Gender</label>
                    <select
                      className="celeb-form-select"
                      value={formData.gender}
                      onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    >
                      {genders.map((g) => (
                        <option key={g} value={g}>
                          {g.charAt(0).toUpperCase() + g.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="celeb-form-group">
                    <label>Nationality / Origin</label>
                    <select
                      className="celeb-form-select"
                      value={formData.nationality}
                      onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                    >
                      {countries.map((ct) => (
                        <option key={ct} value={ct}>
                          {ct.replace('_', ' ').toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="celeb-form-group">
                  <label>Tags (comma separated)</label>
                  <input
                    type="text"
                    className="celeb-form-input"
                    placeholder="e.g. bollywood, elegance, traditional"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  />
                </div>

                <div className="celeb-form-group">
                  <label>Bio / Persona Details</label>
                  <textarea
                    className="celeb-form-textarea"
                    placeholder="Brief styling notes or biography..."
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  />
                </div>

                <div className="celeb-form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      style={{ width: 'auto', accentColor: '#0f172a' }}
                    />
                    <span>Active in AI look & styling recommendations</span>
                  </label>
                </div>
              </form>

              {/* Photo Gallery (Available when editing) */}
              {!isCreating && selectedCeleb && (
                <div style={{ marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                  <div className="celeb-gallery-header">
                    <h3>Photos & Face Embeddings ({getImageCount(selectedCeleb)})</h3>
                    <button
                      type="button"
                      className="celeb-btn-secondary"
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                      disabled={generatingEmbeddings}
                      onClick={handleRegenerateEmbeddings}
                    >
                      {generatingEmbeddings ? 'Queuing...' : '⚡ Regenerate Embeddings'}
                    </button>
                  </div>

                  <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 12px' }}>
                    The first image is used as the default cover. Reorder or upload reference photos.
                  </p>

                  <div className="celeb-image-grid">
                    {(() => {
                      const drawerImages = getImageList(selectedCeleb);
                      return drawerImages.map((img, idx) => {
                        const url = typeof img === 'string' ? img : (img.readable_url || img.url || '');
                        const fname = img.filename || (typeof img === 'string' ? img.split('/').pop() : `img-${idx}`);
                        return (
                          <div key={idx} className={`celeb-image-item ${idx === 0 ? 'primary' : ''}`}>
                            <img src={url} alt={`Celeb ${idx + 1}`} />
                            <div className="celeb-image-overlay">
                              {idx === 0 && <span className="celeb-img-tag">PRIMARY</span>}
                              <div style={{ display: 'flex', gap: '4px', marginTop: 'auto' }}>
                                {idx > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => handleMoveImage(idx, -1)}
                                    style={{ padding: '2px 6px', fontSize: '10px' }}
                                  >
                                    ◀
                                  </button>
                                )}
                                {idx < drawerImages.length - 1 && (
                                  <button
                                    type="button"
                                    onClick={() => handleMoveImage(idx, 1)}
                                    style={{ padding: '2px 6px', fontSize: '10px' }}
                                  >
                                    ▶
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="celeb-img-del-btn"
                                  onClick={() => handleDeleteImage(fname)}
                                  style={{ marginLeft: 'auto' }}
                                >
                                  &times;
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>

                  <div
                    className="celeb-upload-zone"
                    style={{ marginTop: '16px' }}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      multiple
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleFileUpload}
                    />
                    <p>{uploadingImages ? 'Uploading photos...' : '+ Click or drag images here to upload'}</p>
                  </div>

                  {embedJobs && embedJobs.length > 0 && (
                    <div style={{ marginTop: '16px', fontSize: '12px', color: '#64748b' }}>
                      <span style={{ fontWeight: 600 }}>Recent Embed Jobs:</span>
                      <ul style={{ margin: '4px 0 0', paddingLeft: '16px' }}>
                        {embedJobs.slice(0, 3).map((job, jIdx) => (
                          <li key={jIdx}>
                            Status: <span style={{ color: job.status === 'completed' ? '#16a34a' : '#d97706' }}>{job.status}</span> ({job.created_at || 'recent'})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="celeb-drawer-footer">
              <div className="celeb-footer-left">
                {!isCreating && (
                  <button type="button" className="celeb-btn-danger" onClick={handleDelete}>
                    Delete Celebrity
                  </button>
                )}
              </div>
              <div className="celeb-footer-right">
                <button type="button" className="celeb-btn-secondary" onClick={() => setDrawerOpen(false)}>
                  Cancel
                </button>
                <button type="submit" form="celeb-form" className="celeb-btn-primary">
                  {isCreating ? 'Create Celebrity' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CelebritiesManager;
